/* ======== Types ========= */
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";

import { events } from "@constants/tracking";
import { getExpirationDateText } from "@memorang/helpers/src/bundle";
import type { Plan } from "@memorang/types/src/bundle";
import Box from "@memorang/ui/src/components/Box";
/* ======== Components ========= */
import { Text, TouchableRipple } from "react-native-paper";
import { formatPlanDuration } from "./helpers";

export type PlansSectionProps = {
	sortedPlans: Plan[];
	selectedPlan?: Plan;
	setSelectedPlan: (plan: Plan) => void;
	expiryDate?: string;
	handleTracking: (
		eventName: string,
		params?: Record<string, string | number>,
	) => void;
};

type PlanBoxProps = {
	isSelected: boolean;
	planAmount: number;
	alias: string;
	originalAmount: number;
	expirationText: string;
};
const PlansSection = ({
	sortedPlans,
	selectedPlan,
	setSelectedPlan,
	handleTracking,
}: PlansSectionProps) => {
	const theme = useAppTheme();

	const appFont = useAppStore((store) => store.app.font) || "opensans";

	const PlanBox = ({
		isSelected,
		planAmount,
		alias,
		originalAmount,
	}: PlanBoxProps) => {
		return (
			<Box alignItems="center" gap={8}>
				<Text
					variant="titleMedium"
					style={{
						color: isSelected ? theme.colors.primary : theme.colors.text,
						textAlign: "center",
						fontFamily: `${appFont}-semibold`,
					}}
				>
					{`${alias}`}
				</Text>
				<Box alignItems="center">
					{originalAmount !== planAmount && (
						<Text
							variant="labelSmall"
							style={{
								textDecorationLine: "line-through",
							}}
						>{`$${originalAmount}`}</Text>
					)}
					<Text
						variant="titleMedium"
						style={{
							fontFamily: `${appFont}-semibold`,
						}}
					>{`$${planAmount}`}</Text>
				</Box>
			</Box>
		);
	};
	return (
		<Box flexDirection="row" gap={8} justifyContent="center">
			{sortedPlans.map((plan) => {
				const { amount: planAmount, id, originalAmount } = plan;

				const expirationText = getExpirationDateText(plan);

				const isSelected = selectedPlan?.id === id;

				const parsedAlias = formatPlanDuration(plan.interval, plan.duration);

				return (
					<TouchableRipple
						key={id}
						rippleColor="transparent"
						onPress={() => {
							setSelectedPlan(plan);
							handleTracking(events.subscriptionOptionClicked, {
								plan_id: id,
								plan_amount: planAmount,
								plan_duration: plan.duration,
								plan_interval: plan.interval,
								plan_name: plan.name,
								plan_original_amount: originalAmount,
								plan_alias: parsedAlias,
								plan_expiration_text: expirationText,
							});
						}}
						style={{
							borderColor: isSelected
								? theme.colors.primary
								: theme.colors.primaryContainer,
							borderWidth: 2,
							borderRadius: theme.roundness,
							alignItems: "center",
							padding: 16,
							justifyContent: "center",
							paddingHorizontal: 8,
							flexGrow: 1,
							width: 100,
						}}
					>
						<PlanBox
							isSelected={isSelected}
							planAmount={planAmount}
							alias={parsedAlias}
							originalAmount={originalAmount}
							expirationText={expirationText}
						/>
					</TouchableRipple>
				);
			})}
		</Box>
	);
};

export default PlansSection;
